const SITE_URL = 'https://xadevs.com';

export const siteMeta = {
  siteName: 'XADevs LLC',
  legalName: 'XADevs LLC',
  alternateName: ['XADevs', 'XA Devs'],
  siteUrl: SITE_URL,
  language: 'en',
  locale: 'en_US',
  twitterHandle: '@XADevs',
  logoPath: '/logo-xadevs-512.png',
  logoSize: 512,
  defaultImage: {
    path: '/og-image.png',
    width: 1200,
    height: 630,
    alt: 'XADevs LLC: AI Products & Software Systems'
  },
  defaultTitle: 'XADevs LLC | AI Products & Software Systems',
  titleTemplate: '%s | XADevs LLC',
  defaultDescription:
    'XADevs LLC builds AI products and software systems, including VolpeTrack, an AI-powered finance assistant, and HoldThen, a B2B logistics platform.',
  organizationDescription:
    'XADevs LLC is a US-registered software company building AI products and software systems: AI-native product features, cloud backends, and cross-platform apps such as VolpeTrack and HoldThen.',
  emails: {
    general: 'hello@xadevs.com',
    support: 'support@xadevs.com',
    privacy: 'privacy@xadevs.com'
  },
  // Only profiles verified to exist. Developer listings tie the brand to its published apps.
  socialProfiles: [
    'https://x.com/XADevs',
    'https://github.com/xadevs',
    'https://apps.apple.com/us/developer/xadevs-llc/id1827763153',
    'https://play.google.com/store/apps/dev?id=7574937838146582908'
  ],
  importantRoutes: {
    home: '/',
    about: '/about',
    volpeTrack: '/volpetrack',
    holdThen: '/holdthen',
    productsSection: '/#products',
    contactSection: '/#contact',
    support: '/support',
    privacyPolicy: '/privacy-policy',
    termsOfService: '/terms-of-service',
    sitemap: '/sitemap',
    volpeTrackPrivacy: '/volpetrack/privacy',
    volpeTrackTerms: '/volpetrack/terms',
    volpeTrackAccountDeletion: '/volpetrack/account-deletion'
  },
  productUrls: {
    volpeTrackIOS: 'https://apps.apple.com/us/app/volpetrack-ai-budget-tracker/id6748869391',
    volpeTrackAndroid: 'https://play.google.com/store/apps/details?id=com.volpetrack.mobile',
    holdThenWebsite: 'https://www.holdthen.com/'
  },
  routeSeo: {
    home: {
      title: 'XADevs LLC | AI Products & Software Systems',
      description:
        'XADevs LLC builds AI products and software systems. Explore VolpeTrack, an AI-powered finance assistant, and HoldThen, a B2B logistics platform.',
      path: '/'
    },
    about: {
      title: 'About XADevs',
      description:
        'XADevs LLC is a US software company that designs, builds, and operates AI products and software systems, including VolpeTrack and HoldThen.',
      path: '/about',
      breadcrumb: 'About'
    },
    volpeTrack: {
      title: 'VolpeTrack: AI Budget Tracker for iOS and Android',
      description:
        'VolpeTrack is an AI budget tracker for iOS and Android with smart expense categorization, spending reports, goals, and an AI assistant in 10 languages.',
      path: '/volpetrack',
      breadcrumb: 'VolpeTrack',
      image: {
        path: '/og-volpetrack.png',
        width: 1200,
        height: 630,
        alt: 'VolpeTrack: AI budget tracker for iOS and Android'
      }
    },
    holdThen: {
      title: 'HoldThen: B2B International Logistics Platform',
      description:
        'HoldThen is a B2B logistics platform by XADevs LLC that unifies package forwarding, consolidation, inventory, billing, and shipment tracking. Waitlist open.',
      path: '/holdthen',
      breadcrumb: 'HoldThen',
      image: {
        path: '/og-holdthen.png',
        width: 1200,
        height: 630,
        alt: 'HoldThen: B2B international logistics platform'
      }
    },
    support: {
      title: 'Support Center',
      description:
        'Get support for XADevs products, including VolpeTrack and HoldThen, through the official support center.',
      path: '/support',
      breadcrumb: 'Support'
    },
    privacyPolicy: {
      title: 'Privacy Policy',
      description:
        'Read the XADevs LLC privacy policy for website usage, data handling, and communication preferences.',
      path: '/privacy-policy',
      breadcrumb: 'Privacy Policy'
    },
    termsOfService: {
      title: 'Terms of Service',
      description:
        'Read the XADevs LLC terms of service for website use, intellectual property, and legal conditions.',
      path: '/terms-of-service',
      breadcrumb: 'Terms of Service'
    },
    sitemap: {
      title: 'Sitemap',
      description:
        'Browse the XADevs sitemap to access company pages, product pages, support, and legal documentation.',
      path: '/sitemap',
      breadcrumb: 'Sitemap'
    },
    volpeTrackPrivacy: {
      title: 'VolpeTrack Privacy Policy',
      description:
        'Read the VolpeTrack privacy policy by XADevs LLC, including data processing, retention, and user rights.',
      path: '/volpetrack/privacy',
      breadcrumb: 'Privacy Policy',
      parent: 'volpeTrack'
    },
    volpeTrackTerms: {
      title: 'VolpeTrack Terms and Conditions',
      description:
        'Read VolpeTrack terms and conditions, including plans, billing, and usage rules provided by XADevs LLC.',
      path: '/volpetrack/terms',
      breadcrumb: 'Terms and Conditions',
      parent: 'volpeTrack'
    },
    volpeTrackAccountDeletion: {
      title: 'VolpeTrack Account Deletion',
      description:
        'Find official instructions to delete your VolpeTrack account and understand how XADevs LLC processes data removal.',
      path: '/volpetrack/account-deletion',
      breadcrumb: 'Account Deletion',
      parent: 'volpeTrack'
    },
    notFound: {
      title: 'Page Not Found',
      description:
        'The page you requested could not be found. Use the XADevs navigation to find products, support, or company pages.',
      path: '/404'
    }
  }
};

const ensureLeadingSlash = (path = '/') => (path.startsWith('/') ? path : `/${path}`);

export const buildCanonicalUrl = (path = '/') => {
  const normalizedPath = ensureLeadingSlash(path === '' ? '/' : path);
  return new URL(normalizedPath, siteMeta.siteUrl).toString();
};

export const buildPageTitle = (title) => {
  if (!title || title === siteMeta.defaultTitle) {
    return siteMeta.defaultTitle;
  }

  return siteMeta.titleTemplate.replace('%s', title);
};

/* ------------------------------------------------------------------ */
/* Product facts: single source for page copy, FAQs, and JSON-LD.      */
/* Keep these accurate; AI systems quote them.                         */
/* ------------------------------------------------------------------ */

export const volpeTrackFacts = {
  languages: [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pl', name: 'Polish' },
    { code: 'ar', name: 'Arabic' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }
  ],
  features: [
    {
      title: 'Built-in AI assistant',
      text: 'Ask questions about your spending in plain language and get answers based on your own transactions, with optional speech-to-text.'
    },
    {
      title: 'Smart expense categorization',
      text: 'Transactions are organized into categories automatically, so you spend less time on manual bookkeeping.'
    },
    {
      title: 'Spending reports',
      text: 'Monthly summaries and category analysis show where your money goes, for both income and expenses.'
    },
    {
      title: 'Accounts, transfers, and fixed payments',
      text: 'Track several accounts, move money between them, and keep recurring payments in view.'
    },
    {
      title: 'Financial goals',
      text: 'Create savings goals and follow your progress toward each one.'
    },
    {
      title: 'Achievements and badges',
      text: 'Gamification rewards consistent habits with achievements you unlock over time.'
    },
    {
      title: 'Multi-currency',
      text: 'Choose the currency you work in and track your finances in it.'
    }
  ],
  plans: [
    {
      name: 'Free',
      text: 'Core budgeting features at no cost, supported by ads.'
    },
    {
      name: 'Premium Pro',
      text: 'A subscription that unlocks every premium feature, with a 7-day free trial offered in the app.'
    },
    {
      name: 'Founder Pioneer',
      text: 'A one-time purchase with lifetime access to all premium features, limited to the first 100 users, including a founder badge and an optional place on the in-app Founders Wall.'
    }
  ]
};

const languageList = volpeTrackFacts.languages.map((language) => language.name).join(', ');

export const volpeTrackFaq = [
  {
    question: 'What is VolpeTrack?',
    answer:
      'VolpeTrack is an AI-powered budget tracker and personal finance assistant for iOS and Android, built by XADevs LLC. It records income and expenses, categorizes transactions automatically, shows spending reports, tracks financial goals, and includes a built-in AI assistant.'
  },
  {
    question: 'Is VolpeTrack free?',
    answer:
      'Yes. VolpeTrack has a free, ad-supported plan. Premium Pro is an optional subscription with a 7-day free trial offered in the app, and Founder Pioneer is a one-time lifetime purchase limited to the first 100 users.'
  },
  {
    question: 'Which devices does VolpeTrack support?',
    answer:
      'VolpeTrack is available on iPhone through the App Store and on Android phones through Google Play.'
  },
  {
    question: 'Which languages is VolpeTrack available in?',
    answer: `VolpeTrack is available in 10 languages: ${languageList}.`
  },
  {
    question: 'What does the AI in VolpeTrack do?',
    answer:
      'The AI categorizes expenses, analyzes spending patterns, and powers a built-in assistant that answers questions about your own finances in plain language. Voice input through speech-to-text is optional.'
  },
  {
    question: 'How do I delete my VolpeTrack account?',
    answer:
      'You can delete your account from the app settings, or follow the official instructions at xadevs.com/volpetrack/account-deletion.'
  }
];

export const holdThenFacts = {
  features: [
    {
      title: 'Warehouse reception and real-time inventory',
      text: 'Register incoming packages at the warehouse and keep inventory visible as it changes.'
    },
    {
      title: 'International consolidation and shipping',
      text: 'Group packages into consolidated international shipments.'
    },
    {
      title: 'Integrated billing and invoicing',
      text: 'Bill customers and issue invoices from the same platform that handles the operation.'
    },
    {
      title: 'End-to-end traceability',
      text: 'Give both customers and operations teams full visibility of every shipment.'
    }
  ]
};

export const holdThenFaq = [
  {
    question: 'What is HoldThen?',
    answer:
      'HoldThen is a B2B international logistics platform built by XADevs LLC. It unifies package forwarding, consolidation, inventory, billing, and end-to-end shipment tracking in one workflow.'
  },
  {
    question: 'Who is HoldThen for?',
    answer:
      'HoldThen is designed for logistics businesses, such as package forwarders and consolidators, that manage warehouse reception, international shipments, and customer billing.'
  },
  {
    question: 'Is HoldThen available now?',
    answer:
      'HoldThen has not launched yet. Its waitlist is open on the official website, holdthen.com.'
  }
];

/* ------------------------------------------------------------------ */
/* JSON-LD: one connected @graph per page, linked by @id.             */
/* ------------------------------------------------------------------ */

const ids = {
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
  logo: `${SITE_URL}/#logo`,
  volpeTrack: `${SITE_URL}/volpetrack#app`,
  holdThen: `${SITE_URL}/holdthen#app`
};

const organizationNode = {
  '@type': 'Organization',
  '@id': ids.organization,
  name: siteMeta.siteName,
  legalName: siteMeta.legalName,
  alternateName: siteMeta.alternateName,
  url: `${SITE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    '@id': ids.logo,
    url: buildCanonicalUrl(siteMeta.logoPath),
    contentUrl: buildCanonicalUrl(siteMeta.logoPath),
    width: siteMeta.logoSize,
    height: siteMeta.logoSize,
    caption: 'XADevs logo'
  },
  image: { '@id': ids.logo },
  description: siteMeta.organizationDescription,
  email: siteMeta.emails.general,
  // Same address and phone shown in the site footer.
  telephone: '+1-307-556-7770',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '30 N Gould St #N',
    addressLocality: 'Sheridan',
    addressRegion: 'WY',
    postalCode: '82801',
    addressCountry: 'US'
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: siteMeta.emails.support,
      url: buildCanonicalUrl('/support'),
      availableLanguage: ['English', 'Spanish']
    },
    {
      '@type': 'ContactPoint',
      contactType: 'privacy',
      email: siteMeta.emails.privacy
    }
  ],
  sameAs: siteMeta.socialProfiles,
  knowsAbout: [
    'Artificial intelligence',
    'Generative AI',
    'AI assistants',
    'Software engineering',
    'Cloud infrastructure',
    'Mobile applications',
    'Personal finance software',
    'Logistics software'
  ]
};

const websiteNode = {
  '@type': 'WebSite',
  '@id': ids.website,
  url: `${SITE_URL}/`,
  name: siteMeta.siteName,
  alternateName: siteMeta.alternateName,
  description: siteMeta.defaultDescription,
  inLanguage: siteMeta.language,
  publisher: { '@id': ids.organization }
};

const volpeTrackNode = {
  '@type': 'MobileApplication',
  '@id': ids.volpeTrack,
  name: 'VolpeTrack',
  alternateName: 'VolpeTrack: AI Budget Tracker',
  url: buildCanonicalUrl('/volpetrack'),
  image: buildCanonicalUrl('/logo-volpetrack.png'),
  applicationCategory: 'FinanceApplication',
  applicationSubCategory: 'Budget tracker',
  operatingSystem: 'iOS, Android',
  description:
    'AI-powered budget tracker and personal finance assistant with smart expense categorization, spending reports, financial goals, and a built-in AI assistant.',
  featureList: volpeTrackFacts.features.map((feature) => feature.title),
  inLanguage: volpeTrackFacts.languages.map((language) => language.code),
  installUrl: [siteMeta.productUrls.volpeTrackIOS, siteMeta.productUrls.volpeTrackAndroid],
  sameAs: [siteMeta.productUrls.volpeTrackIOS, siteMeta.productUrls.volpeTrackAndroid],
  offers: {
    '@type': 'Offer',
    name: 'Free plan',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock'
  },
  publisher: { '@id': ids.organization },
  author: { '@id': ids.organization }
};

const holdThenNode = {
  '@type': 'SoftwareApplication',
  '@id': ids.holdThen,
  name: 'HoldThen',
  url: siteMeta.productUrls.holdThenWebsite,
  image: buildCanonicalUrl('/logo-holdthen-icon.png'),
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Logistics management',
  operatingSystem: 'Web',
  description:
    'B2B international logistics platform unifying package forwarding, consolidation, inventory, billing, and end-to-end shipment tracking.',
  featureList: holdThenFacts.features.map((feature) => feature.title),
  publisher: { '@id': ids.organization },
  author: { '@id': ids.organization }
};

const faqNode = (url, faq) => ({
  '@type': 'FAQPage',
  '@id': `${url}#faq`,
  mainEntity: faq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer }
  }))
});

const breadcrumbNode = (routeKey) => {
  const route = siteMeta.routeSeo[routeKey];
  const items = [{ name: 'Home', url: `${SITE_URL}/` }];
  if (route.parent) {
    const parent = siteMeta.routeSeo[route.parent];
    items.push({ name: parent.breadcrumb, url: buildCanonicalUrl(parent.path) });
  }
  items.push({ name: route.breadcrumb, url: buildCanonicalUrl(route.path) });

  return {
    '@type': 'BreadcrumbList',
    '@id': `${buildCanonicalUrl(route.path)}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};

const PAGE_TYPES = {
  about: 'AboutPage',
  support: 'ContactPage',
  sitemap: 'CollectionPage'
};

/**
 * Builds the JSON-LD graph for a route: Organization + WebSite on every page,
 * plus that page's WebPage, breadcrumb, and any product or FAQ entities.
 */
export const buildStructuredData = (routeKey) => {
  const route = siteMeta.routeSeo[routeKey];
  const url = buildCanonicalUrl(route.path);
  const image = route.image || siteMeta.defaultImage;
  const isHome = routeKey === 'home';

  const webPage = {
    '@type': PAGE_TYPES[routeKey] || 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: buildPageTitle(route.title),
    description: route.description,
    inLanguage: siteMeta.language,
    isPartOf: { '@id': ids.website },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: buildCanonicalUrl(image.path),
      width: image.width,
      height: image.height
    },
    about: { '@id': ids.organization }
  };

  const graph = [organizationNode, websiteNode, webPage];

  if (!isHome) {
    const breadcrumb = breadcrumbNode(routeKey);
    webPage.breadcrumb = { '@id': breadcrumb['@id'] };
    graph.push(breadcrumb);
  }

  if (isHome) {
    webPage.mainEntity = { '@id': ids.organization };
    graph.push(volpeTrackNode, holdThenNode);
  }

  if (routeKey === 'volpeTrack' || route.parent === 'volpeTrack') {
    webPage.about = { '@id': ids.volpeTrack };
    graph.push(volpeTrackNode);
  }

  if (routeKey === 'volpeTrack') {
    webPage.mainEntity = { '@id': ids.volpeTrack };
    graph.push(faqNode(url, volpeTrackFaq));
  }

  if (routeKey === 'holdThen') {
    webPage.about = { '@id': ids.holdThen };
    webPage.mainEntity = { '@id': ids.holdThen };
    graph.push(holdThenNode, faqNode(url, holdThenFaq));
  }

  if (routeKey === 'about') {
    webPage.mainEntity = { '@id': ids.organization };
  }

  return [{ '@context': 'https://schema.org', '@graph': graph }];
};

// Kept for backwards compatibility with existing imports.
export const homepageStructuredData = buildStructuredData('home');
